import React, {useEffect, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {NavLink, useLocation} from 'react-router-dom';
import wordLogo from '../../assets/persnormal-word-logo.svg';
import eatIcon from '../../assets/eat_slowly_icon.svg';
import spendIcon from '../../assets/spend_gently_icon.png';
import fridgeIcon from '../../assets/fridge_organizer_icon.webp';
import './NavBar.css';

const NavBar = () => {
    const {t, i18n} = useTranslation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const location = useLocation();

    useEffect(() => {
        setIsMenuOpen(false);
        setIsDropdownOpen(false);
    }, [location.pathname]);

    useEffect(() => {
        document.body.style.overflow = isMenuOpen ? 'hidden' : '';
        return () => {
            document.body.style.overflow = '';
        };
    }, [isMenuOpen]);

    const getNavLinkClass = ({isActive}) => (
        isActive ? 'nav-link active' : 'nav-link'
    );

    const changeLanguage = (language) => {
        i18n.changeLanguage(language);
        setIsMenuOpen(false);
    };

    const products = [
        {path: '/eat-slowly', icon: eatIcon, nameKey: 'eat_slowly_name', taglineKey: 'eat_slowly_tagline'},
        {path: '/spend-gently', icon: spendIcon, nameKey: 'spend_gently_name', taglineKey: 'spend_gently_tagline'},
        {path: '/fridge-organizer', icon: fridgeIcon, nameKey: 'fridge_organizer_name', taglineKey: 'fridge_organizer_tagline'},
    ];

    const isProductActive = ['/eat-slowly', '/spend-gently', '/fridge-organizer'].includes(location.pathname);

    return (
        <header className="navbar">
            <nav className="navbar-inner container" aria-label={t('nav.aria')}>
                <NavLink to="/" className="navbar-logo" onClick={() => setIsMenuOpen(false)}>
                    <img src={wordLogo} alt={t('company.logo_alt')}/>
                </NavLink>

                <div className="navbar-links">
                    <NavLink to="/" end className={getNavLinkClass}>
                        {t('nav.home')}
                    </NavLink>

                    <div
                        className="nav-dropdown"
                        onMouseEnter={() => setIsDropdownOpen(true)}
                        onMouseLeave={() => setIsDropdownOpen(false)}
                    >
                        <button
                            type="button"
                            className={`nav-link nav-dropdown-trigger${isProductActive ? ' active' : ''}`}
                            aria-haspopup="true"
                            aria-expanded={isDropdownOpen}
                            onClick={() => setIsDropdownOpen((open) => !open)}
                            onFocus={() => setIsDropdownOpen(true)}
                        >
                            {t('nav.products')}
                            <svg className="nav-caret" width="8" height="5" viewBox="0 0 8 5" aria-hidden="true">
                                <path d="M1 1l3 3 3-3" fill="none" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                        </button>

                        {isDropdownOpen && (
                            <div className="nav-dropdown-panel" onFocus={() => setIsDropdownOpen(true)}>
                                {products.map((product) => (
                                    <NavLink
                                        to={product.path}
                                        className="nav-dropdown-item"
                                        key={product.path}
                                        onClick={() => setIsDropdownOpen(false)}
                                    >
                                        <img src={product.icon} alt=""/>
                                        <span>
                                            <strong>{t(product.nameKey)}</strong>
                                            <small>{t(product.taglineKey)}</small>
                                        </span>
                                    </NavLink>
                                ))}
                            </div>
                        )}
                    </div>

                    <NavLink to="/contact" className={getNavLinkClass}>
                        {t('nav.contact')}
                    </NavLink>
                </div>

                <div className="navbar-side">
                    <div className="lang-toggle" aria-label={t('nav.language')}>
                        <button
                            type="button"
                            onClick={() => changeLanguage('ko')}
                            className={i18n.language.startsWith('ko') ? 'active' : ''}
                            aria-pressed={i18n.language.startsWith('ko')}
                        >
                            KO
                        </button>
                        <span aria-hidden="true">/</span>
                        <button
                            type="button"
                            onClick={() => changeLanguage('en')}
                            className={i18n.language.startsWith('en') ? 'active' : ''}
                            aria-pressed={i18n.language.startsWith('en')}
                        >
                            EN
                        </button>
                    </div>

                    <button
                        type="button"
                        className={`navbar-toggle${isMenuOpen ? ' open' : ''}`}
                        onClick={() => setIsMenuOpen((open) => !open)}
                        aria-label={t('nav.menu')}
                        aria-expanded={isMenuOpen}
                    >
                        <span/>
                        <span/>
                    </button>
                </div>
            </nav>

            {isMenuOpen && (
                <div className="navbar-sheet">
                    <div className="container">
                        <NavLink to="/" end className={getNavLinkClass}>
                            {t('nav.home')}
                        </NavLink>
                        <span className="sheet-group-label">{t('nav.products')}</span>
                        {products.map((product) => (
                            <NavLink to={product.path} className="nav-link sheet-product" key={product.path}>
                                <img src={product.icon} alt=""/>
                                <span>
                                    {t(product.nameKey)}
                                    <small>{t(product.taglineKey)}</small>
                                </span>
                            </NavLink>
                        ))}
                        <NavLink to="/contact" className={getNavLinkClass}>
                            {t('nav.contact')}
                        </NavLink>
                    </div>
                </div>
            )}
        </header>
    );
};

export default NavBar;
